import { squish } from 'picsquish';
import { thumbHashToAverageRGBA as _thumbHashToAverageRGBA, thumbHashToDataURL as _thumbHashToDataURL, rgbaToThumbHash } from 'thumbhash';

/**
 * Converts a binary Uint8Array to a base64 string
 * Extracted from https://github.com/evanw/thumbhash/blob/main/examples/browser/index.html
 * @param {Uint8Array} binary - The binary array to convert
 * @returns {string} The base64 string
 */
const binaryToBase64 = (binary) => btoa(String.fromCharCode(...binary));

/**
 * Converts a base64 string to a binary Uint8Array
 * Extracted from https://github.com/evanw/thumbhash/blob/main/examples/browser/index.html
 * @param {string} base64 - The base64 string to convert
 * @returns {Uint8Array} The binary array
 */
const base64ToBinary = (base64) => {
  return new Uint8Array(
    atob(base64)
      .split('')
      .map((x) => x.charCodeAt(0)),
  );
};

/**
 * Converts a thumbhash string as a base64 string to a data URL
 * @param {string} thumbhash - The base64 encoded thumbhash
 * @returns {string} The data URL of the thumbhash
 */
export const thumbhashToDataURL = (thumbhash) => {
  return _thumbHashToDataURL(base64ToBinary(thumbhash));
};

/**
 * Converts a thumbhash string as a base64 string to an average RGBA color
 * @param {string} thumbhash - The base64 encoded thumbhash
 * @returns {{ r: number, g: number, b: number, a: number }} The average RGBA color (values 0-1)
 */
export const thumbhashToAverageRGBA = (thumbhash) => {
  return _thumbHashToAverageRGBA(base64ToBinary(thumbhash));
};

const DEFAULT_TINT_RGBA = { r: 0.75, g: 0.75, b: 0.75 };

/**
 * @param {{ r: number, g: number, b: number, a?: number }} rgba - components 0–1
 * @returns {string} CSS rgba string at 50% alpha (soft tint)
 */
export const averageRgbaToSoftTint = (rgba) => {
  return `rgba(${Math.round(rgba.r * 255)}, ${Math.round(rgba.g * 255)}, ${Math.round(rgba.b * 255)}, 0.5)`;
};

/**
 * Converts a thumbhash to a soft CSS background-color string (50% alpha)
 * @param {string|null} thumbhash - The base64 encoded thumbhash, or null
 * @returns {string} CSS rgba color string
 */
export const thumbhashToSoftTint = (thumbhash) => {
  let rgba = DEFAULT_TINT_RGBA;
  if (thumbhash) {
    try {
      rgba = thumbhashToAverageRGBA(thumbhash);
    } catch {
      // fall through to default
    }
  }
  return averageRgbaToSoftTint(rgba);
};

/**
 * Converts RGBA pixel data from squish() to a JPEG Blob
 * @param {{ width: number, height: number, raw: Uint8Array }} rgbaData - RGBA pixel data from picsquish
 * @param {string} mimeType - Output MIME type (default: 'image/jpeg')
 * @param {number} quality - JPEG quality 0-1 (default: 0.85)
 * @returns {Promise<Blob>} The image as a Blob
 */
const rgbaToBlob = async ({ width, height, raw }, mimeType = 'image/jpeg', quality = 0.85) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const imageData = new ImageData(new Uint8ClampedArray(raw), width, height);
  ctx.putImageData(imageData, 0, 0);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create image blob'));
        }
      },
      mimeType,
      quality,
    );
  });
};

/**
 * Generates thumbhash metadata AND a local 640px thumbnail Blob for storage
 * @param {File} file - Image file to process
 * @returns {Promise<{ thumbhash: string, thumbsize: string, thumbnailBlob: Blob }>}
 * @throws {Error} If the file does not have an image/* mime type
 */
export const generateLocalThumbnailData = async (file) => {
  if (!file.type?.toLowerCase().startsWith('image/')) {
    throw new Error('File is not an image');
  }

  const sizes = squish(file, [100, 640]);
  const [thumbhashSource, thumbnailSource] = await Promise.all([sizes[0], sizes[1]]);

  const thumbhash = rgbaToThumbHash(thumbhashSource.width, thumbhashSource.height, thumbhashSource.raw);

  // Preserve transparency: PNG/GIF/WebP -> WebP (better compression than PNG), others -> JPEG
  const transparentFormats = ['image/png', 'image/gif', 'image/webp'];
  const outputMimeType = transparentFormats.includes(file.type.toLowerCase()) ? 'image/webp' : 'image/jpeg';
  const thumbnailBlob = await rgbaToBlob(thumbnailSource, outputMimeType);

  return {
    thumbhash: binaryToBase64(thumbhash),
    thumbsize: `${thumbnailSource.width}x${thumbnailSource.height}`,
    thumbnailBlob,
  };
};
